'use client';

import { useMemo } from 'react';

import type { CareStateRowUpdatedEventT } from '@lotushealth/lotushealth_js_sdk/schemas';
import type { FetchTimelineResponseT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { useCareTimelineInfiniteQuery } from '@/entities/care-timeline';
import { cn } from '@/lib/utils';
import { toDayKeyYYYYMMDD, toTimestampMs } from '@/shared/lib';
import { Button } from '@/shared/ui/shadcn/button';
import {
  EmptyState,
  SkeletonTextBlock,
  TimelineViewer,
  formatTimelineTimeLabel,
  mapToDaySections,
} from '@/shared/ui/primitives';

type TimelineEventItem = CareStateRowUpdatedEventT & {
  __key: string;
  __timestampMs: number | null;
  __dayKey: string | null;
  __timeLabel: string;
};

function renderEvent(event: TimelineEventItem) {
  return (
    <div className="space-y-0.5">
      <div className="text-sm text-foreground">{event.message}</div>
      <div className="text-xs text-muted-foreground">
        {event.__timeLabel} · {event.identity_display} · {event.context.table}[{event.context.row}]
      </div>
    </div>
  );
}

export function CareStateTimeline({ className }: { className?: string }) {
  const { patientUserId } = useCareStateDomain();

  const query = useCareTimelineInfiniteQuery(patientUserId, { limit: 50 });

  const sections = useMemo(() => {
    const pages = query.data?.pages ?? [];
    const events = pages.flatMap((p: FetchTimelineResponseT) => p.events);

    return mapToDaySections(events, (event, index): TimelineEventItem => {
      const raw = event.when;
      const ms = toTimestampMs(raw);
      return {
        ...event,
        __key: [
          event.context.table,
          event.context.row,
          event.when,
          event.identity_display,
          event.message,
          String(index),
        ].join(':'),
        __timestampMs: ms,
        __dayKey: toDayKeyYYYYMMDD(raw),
        __timeLabel: formatTimelineTimeLabel(ms, raw),
      };
    });
  }, [query.data?.pages]);

  const canLoadOlder = (query.data?.pages.at(-1)?.beforeCursor ?? null) !== null;

  if (query.isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        <div className="text-sm font-medium text-foreground">Timeline</div>
        <SkeletonTextBlock lines={6} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={className}>
        <EmptyState
          title="Could not load timeline"
          description={
            query.error instanceof Error ? query.error.message : 'An unknown error occurred.'
          }
        />
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <TimelineViewer
        sections={sections}
        getItemKey={(item) => item.__key}
        renderItem={renderEvent}
        empty={
          <EmptyState
            title="No timeline events"
            description="No care state timeline activity was returned for this patient."
          />
        }
      />

      {canLoadOlder ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => query.fetchNextPage()}
            disabled={!query.hasNextPage || query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load older'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
